import React, { useState, useEffect } from 'react';
import { useContractServices } from '../../context/ContractServicesContext';

const DebugTreasuryPanel = () => {
  const { services, isReady } = useContractServices();
  const [logs, setLogs] = useState([]);
  const [contractInfo, setContractInfo] = useState(null);
  
  const addLog = (message) => {
    setLogs(prevLogs => [...prevLogs, `${new Date().toISOString()}: ${message}`]);
  };
  
  useEffect(() => {
    if (isReady) {
      addLog('Contract services ready');
      inspectServices();
    } else {
      addLog('Waiting for contract services...');
    }
  }, [isReady]);
  
  const inspectServices = () => {
    try {
      addLog('Inspecting services...');
      
      // Log available services
      const serviceNames = Object.keys(services);
      addLog(`Available services: ${serviceNames.join(', ')}`);
      
      // Check protocolTreasury service
      if (services.protocolTreasury) {
        addLog('Found protocolTreasury service');
        
        // Log the service properties
        const properties = Object.getOwnPropertyNames(services.protocolTreasury);
        addLog(`Properties: ${properties.join(', ')}`);
        
        // Log the prototype methods
        const methods = Object.getOwnPropertyNames(Object.getPrototypeOf(services.protocolTreasury));
        addLog(`Methods: ${methods.join(', ')}`);
        
        // Check contract
        if (services.protocolTreasury.contract) {
          addLog(`Contract address: ${services.protocolTreasury.contract.address}`);
          
          // Log the contract methods
          const contractFunctions = Object.keys(services.protocolTreasury.contract.functions);
          addLog(`Contract functions: ${contractFunctions.join(', ')}`);
          
          setContractInfo({
            address: services.protocolTreasury.contract.address,
            functions: contractFunctions
          });
        } else {
          addLog('No contract found in protocolTreasury service');
        }
      } else {
        addLog('protocolTreasury service not found');
      }
    } catch (error) {
      addLog(`Error during inspection: ${error.message}`);
      console.error('Error during service inspection:', error);
    }
  };
  
  const testGetBalance = async () => {
    try {
      addLog('Testing getBalance...');
      
      if (!services.protocolTreasury) {
        addLog('protocolTreasury service not found');
        return;
      }
      
      if (typeof services.protocolTreasury.getBalance === 'function') {
        addLog('Calling protocolTreasury.getBalance()');
        const balance = await services.protocolTreasury.getBalance();
        addLog(`Balance from service method: ${balance}`);
      } else {
        addLog('getBalance method not found on service');
      }
      
      if (services.protocolTreasury.contract && typeof services.protocolTreasury.contract.getBalance === 'function') {
        addLog('Calling protocolTreasury.contract.getBalance()');
        const balanceRaw = await services.protocolTreasury.contract.getBalance();
        const balance = balanceRaw.toString();
        addLog(`Balance from contract call: ${balance}`);
      } else {
        addLog('getBalance method not found on contract');
      }
    } catch (error) {
      addLog(`Error getting balance: ${error.message}`);
      console.error('Error getting balance:', error);
    }
  };
  
  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Protocol Treasury Debug</h2>
      
      <div className="mb-4">
        <button 
          onClick={inspectServices}
          className="mr-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Inspect Services
        </button>
        
        <button 
          onClick={testGetBalance}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Test getBalance
        </button>
      </div>
      
      {contractInfo && (
        <div className="mb-4 p-4 bg-gray-100 rounded">
          <h3 className="font-medium">Contract Info</h3>
          <p>Address: {contractInfo.address}</p>
          <div>
            <p className="font-medium mt-2">Functions:</p>
            <ul className="list-disc pl-5">
              {contractInfo.functions.map((fn, index) => (
                <li key={index}>{fn}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      <div>
        <h3 className="font-medium mb-2">Debug Logs</h3>
        <div className="bg-gray-900 text-white p-3 rounded h-64 overflow-y-auto">
          {logs.map((log, index) => (
            <div key={index} className="mb-1">
              <code>{log}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DebugTreasuryPanel; 